import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Orbit, Search, Leaf, BookOpen, Phone, MessageCircle, Calendar, ShoppingBag, ChevronDown } from "lucide-react";
import { getPlanets, getCases, getBlog } from "../lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1515705576963-95cad62945b6";

const PHONE = "+919559093555";
const WHATSAPP_LINK = "https://wa.me/919559093555?text=Hello%20Dr.%20Manish%2C%20I%20would%20like%20to%20know%20more%20about%20Astrohomeopathy%C2%AE.";
const AMAZON_LINK = "https://www.amazon.in/s?k=Astrohomoeopathy+and+Lunar+Phases+Manish+Misra";

const FAQS = [
  { q: "What is Astrohomeopathy®?", a: "Astrohomeopathy® is Dr. Manish Misra's integrated approach that combines classical Hahnemannian homeopathy with the diagnostic framework of Vedic astrology. The natal chart is studied as an adjunct to case-taking; remedies are prescribed on the totality of symptoms and constitutional picture." },
  { q: "Is homeopathy safe?", a: "Classical homeopathy uses highly diluted natural substances and, when prescribed by a qualified physician, is broadly considered safe. Dr. Misra is a BHMS graduate with postgraduate training in London and follows classical prescribing protocols." },
  { q: "Do I need my birth details for a consultation?", a: "Birth date, time and place are useful for astrological chart preparation and enhance the constitutional assessment, but they are not mandatory — a detailed case-taking on symptoms remains central to prescription." },
  { q: "Can I consult from outside India?", a: "Yes. Online video consultations are available worldwide. Please reach out via WhatsApp at +91 95590 93555 to schedule." },
  { q: "How long does treatment take?", a: "Chronic conditions typically require several months of follow-up, with periodic assessment and adjustment. Individual responses vary and no specific outcomes are guaranteed." },
  { q: "Where is the clinic located?", a: "Dr. Manish Homeopathic Clinic, Shop No. 6, Ground Floor, LDA Complex, Vishwas Khand-3, Gomti Nagar, Lucknow – 226010, Uttar Pradesh, India." },
];

export default function Home() {
  const [planets, setPlanets] = useState([]);
  const [cases, setCases] = useState([]);
  const [blog, setBlog] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    getPlanets().then(setPlanets).catch(() => {});
    getCases().then((c) => setCases(c.slice(0, 3))).catch(() => {});
    getBlog().then(setBlog).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Cosmos" className="w-full h-full object-cover opacity-[0.18]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/85 to-white" />
          <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#0EA5E9]/10 to-[#D4AF37]/10 blur-3xl" />
        </div>

        <div className="container-page relative pt-16 md:pt-24 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <Sparkles size={14} className="text-[#D4AF37]" />
                <span className="text-xs tracking-[0.24em] uppercase text-[#0F172A] font-medium">Official Research & Education Portal</span>
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#0F172A] font-serif" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Astrohomeopathy<sup className="text-[#D4AF37] text-2xl md:text-3xl">®</sup>
                <br />
                <span className="text-[#0EA5E9] italic">by Dr. Manish Misra</span>
              </h1>
              <p className="mt-6 md:mt-8 text-base md:text-xl text-[#475569] max-w-xl leading-relaxed">
                Classical homeopathic care integrated with Vedic astrological insight — practised in Lucknow, offered worldwide via online consultation.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact#book" data-testid="hero-book-cta" className="btn-gold text-sm md:text-base">
                  <Calendar size={18} className="mr-2" /> Book Appointment
                </Link>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta" className="inline-flex items-center justify-center rounded-full bg-[#25D366] text-white font-medium px-6 py-3 hover:bg-[#1EBE5A] transition text-sm md:text-base">
                  <MessageCircle size={18} className="mr-2" /> WhatsApp
                </a>
                <a href={`tel:${PHONE}`} data-testid="hero-call-cta" className="btn-sky text-sm md:text-base">
                  <Phone size={18} className="mr-2" /> Call Now
                </a>
              </div>

              <div className="mt-8 md:mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs md:text-sm">
                <span className="text-[#0F172A]"><span className="text-[#D4AF37] font-semibold">BHMS</span></span>
                <span className="text-slate-300">·</span>
                <span className="text-[#0F172A]"><span className="text-[#D4AF37] font-semibold">PG Hom. (London)</span></span>
                <span className="text-slate-300">·</span>
                <span className="text-[#0F172A]"><span className="text-[#D4AF37] font-semibold">M.A. Astrology</span>, Univ. of Lucknow</span>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
              <img src="https://customer-assets.emergentagent.com/job_astrohomeo-pro/artifacts/3622mtu8_121460.jpg" alt="Dr. Manish Misra — Classical Homeopathic Physician" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0F172A]/70" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-1">Founder & Principal Physician</div>
                <div className="font-serif text-2xl md:text-3xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Dr. Manish Misra</div>
                <div className="text-[11px] text-white/80 mt-1">BHMS · PG Hom. (London) · M.A. Astrology</div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center animate-rotate-slow">
                <Sparkles size={28} className="text-[#D4AF37]" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO ABOUT ASTROHOMEOPATHY */}
      <section className="section bg-[#F0F9FF]/60">
        <div className="container-page grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">Introduction</div>
            <h2 className="text-3xl md:text-5xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What is Astrohomeopathy<sup className="text-[#D4AF37]">®</sup>?
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-base md:text-lg text-[#475569] leading-relaxed">
              Astrohomeopathy<sup className="text-[#D4AF37]">®</sup> is Dr. Manish Misra&rsquo;s integrated framework that studies the natal chart alongside classical homeopathic case-taking. Planetary influences are treated as diagnostic indicators; remedies are chosen on the totality of symptoms and the constitutional picture, following Hahnemannian principles.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
              The result is a rigorous, individualised approach — grounded in classical homeopathy and enriched by the interpretive depth of Vedic astrology.
            </p>
            <Link to="/what-is-astrohomeopathy" data-testid="home-learnmore-cta" className="btn-gold mt-8">Learn More <ArrowRight size={16} className="ml-2" /></Link>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section">
        <div className="container-page">
          <div className="max-w-2xl mb-12 md:mb-16">
            <div className="eyebrow mb-4">The Framework</div>
            <h2 className="text-3xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Three pillars</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Orbit, title: "Astrological Assessment", desc: "The natal chart informs the diagnostic framework — planetary indicators support constitutional understanding." },
              { icon: Leaf, title: "Classical Homeopathy", desc: "Hahnemannian principles: totality of symptoms, simillimum, minimum dose, single remedy." },
              { icon: Search, title: "Integrated Prescription", desc: "Remedy chosen at the intersection of the symptom picture and the planetary indication." },
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card p-8">
                <div className="w-12 h-12 rounded-2xl bg-[#F0F9FF] flex items-center justify-center text-[#0EA5E9] mb-6">
                  <p.icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK */}
      <section className="section bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_left,#0EA5E9_0%,transparent_50%),radial-gradient(circle_at_bottom_right,#D4AF37_0%,transparent_50%)]" />
        <div className="container-page relative grid md:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center order-1 md:order-1">
            <div className="relative w-56 sm:w-64 md:w-72 aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <img src="https://customer-assets.emergentagent.com/job_astrohomeo-pro/artifacts/ph9mevmc_121462.webp" alt="Astrohomoeopathy and Lunar Phases — book cover" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="order-2 md:order-2">
            <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Featured Publication</div>
            <h2 className="text-3xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Astrohomoeopathy and Lunar Phases</h2>
            <p className="text-white/80 leading-relaxed mb-4">A foundational text co-authored by <strong className="text-white">Dr. Manish Misra</strong> and <strong className="text-white">Dr. Maneesh Mishra</strong>, exploring the correspondence between lunar phases, planetary influences, and homeopathic prescription.</p>
            <div className="flex items-center gap-2 text-sm text-white/70 mb-8">
              <BookOpen size={16} className="text-[#D4AF37]" />
              <span>Authors: Dr. Manish Misra · Dr. Maneesh Mishra</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={AMAZON_LINK} target="_blank" rel="noopener noreferrer" data-testid="home-buy-amazon" className="inline-flex items-center justify-center rounded-full bg-[#FF9900] hover:bg-[#E88B00] text-[#0F172A] font-semibold px-6 py-3 transition text-sm md:text-base">
                <ShoppingBag size={18} className="mr-2" /> Buy on Amazon
              </a>
              <Link to="/about#books" data-testid="home-book-details" className="btn-sky !bg-transparent !border-white/20 !text-white hover:!bg-white/10">
                Book Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PLANET PREVIEW */}
      <section className="section">
        <div className="container-page">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-14 gap-6">
            <div className="max-w-xl">
              <div className="eyebrow mb-4">The Nine Grahas</div>
              <h2 className="text-3xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Planet & Remedy Explorer</h2>
            </div>
            <Link to="/planets" data-testid="home-planets-cta" className="btn-gold self-start md:self-auto">Explore all <ArrowRight size={16} className="ml-2" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {planets.slice(0, 5).map((p, i) => (
              <Link key={p.id} to={`/planets#${p.id}`} data-testid={`home-planet-${p.id}`} className="group relative aspect-square rounded-3xl border border-slate-100 bg-white hover:border-[#D4AF37] hover:shadow-lg transition-all p-5 flex flex-col justify-between">
                <div className="text-4xl" style={{ color: p.color }}>{p.symbol}</div>
                <div>
                  <div className="text-xs tracking-wider text-[#475569] uppercase">Graha {i + 1}</div>
                  <div className="text-sm mt-1 font-medium text-[#0F172A]">{p.name.split(" ")[0]}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="section bg-[#F0F9FF]/60">
        <div className="container-page">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-14 gap-6">
            <div>
              <div className="eyebrow mb-4">Educational Illustrations</div>
              <h2 className="text-3xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Featured case studies</h2>
              <p className="text-sm text-[#475569] mt-3 italic">Illustrative teaching cases — not records of specific patients</p>
            </div>
            <Link to="/case-studies" data-testid="home-cases-cta" className="btn-sky self-start">View all</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {cases.map((c) => (
              <Link key={c.id} to="/case-studies" data-testid={`home-case-${c.id}`} className="group block rounded-3xl overflow-hidden border border-slate-100 bg-white hover:shadow-xl transition-shadow">
                <div className="aspect-[4/3] bg-gradient-to-br from-[#F0F9FF] via-white to-[#FEF9E7] flex items-center justify-center">
                  <div className="text-6xl text-[#D4AF37]/60">☉</div>
                </div>
                <div className="p-6">
                  <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-2">{c.planet}</div>
                  <h3 className="text-xl group-hover:text-[#0EA5E9] transition leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{c.title}</h3>
                  <div className="text-sm text-[#475569] mt-3">Teaching case scenario</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST BLOG */}
      <section className="section">
        <div className="container-page">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-14 gap-6">
            <div>
              <div className="eyebrow mb-4">Journal</div>
              <h2 className="text-3xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Latest writings</h2>
            </div>
            <Link to="/blog" data-testid="home-blog-cta" className="btn-sky self-start">All articles</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blog.map((post) => (
              <Link key={post.id} to="/blog" data-testid={`home-blog-${post.id}`} className="group block rounded-3xl overflow-hidden border border-slate-100 bg-white hover:shadow-xl transition-shadow">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-2">{post.category}</div>
                  <h3 className="text-xl group-hover:text-[#0EA5E9] transition leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{post.title}</h3>
                  <div className="mt-3 flex gap-3 text-xs text-[#475569]">
                    <span>{post.date}</span>·<span>{post.read_time}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-[#F0F9FF]/60">
        <div className="container-page grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-4">Common Questions</div>
            <h2 className="text-3xl md:text-5xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Frequently asked</h2>
            <p className="text-[#475569] leading-relaxed">Everything international patients typically ask about the consultation, safety, and practicalities.</p>
          </div>
          <div className="md:col-span-8 space-y-3" data-testid="home-faq">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className={`rounded-2xl border transition ${open ? "border-[#D4AF37] bg-white shadow-lg" : "border-slate-200 bg-white"}`}>
                  <button
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-medium text-[#0F172A] text-base md:text-lg pr-4">{f.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-[#0EA5E9] transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 md:px-6 pb-6 text-[#475569] text-sm md:text-base leading-relaxed" data-testid={`faq-answer-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-page">
          <div className="relative rounded-[2rem] md:rounded-[3rem] overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#0EA5E9] p-8 md:p-16 lg:p-20 text-white">
            <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1515705576963-95cad62945b6')] bg-cover" />
            <div className="relative max-w-2xl">
              <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Begin your consultation</div>
              <h2 className="text-3xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Consult with Dr. Manish Misra</h2>
              <p className="text-white/80 mb-2">Dr. Manish Homeopathic Clinic · Gomti Nagar, Lucknow</p>
              <p className="text-white/60 text-sm mb-8">WhatsApp / Mobile: +91 9559093555</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact#book" data-testid="home-cta-book" className="btn-gold"><Calendar size={16} className="mr-2" /> Book Appointment</Link>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="home-cta-whatsapp" className="inline-flex items-center justify-center rounded-full bg-[#25D366] text-white font-medium px-6 py-3 hover:bg-[#1EBE5A] transition">
                  <MessageCircle size={16} className="mr-2" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
